// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { RouteErrorBoundary } from '@/components/app-shell/RouteErrorBoundary';

import { Logo } from '@/components/greenlight/Logo';
import { GLSheetGrabHandle } from '@/components/greenlight/GLDrawer';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PreviewFeaturesMenu } from '@/components/PreviewFeaturesMenu';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useCurrentUser } from '@/lib/auth';
import { bottomNavSlots, visibleNavItems, type NavItem } from '@/lib/nav';
import { isPreviewFeatureEnabled } from '@/lib/preview-features';
import { useSheetDrag } from '@/lib/useSheetDrag';
import { cn } from '@/lib/utils';

import { FeedbackDialog } from './FeedbackDialog';
import { FleetReportingBanner } from './FleetReportingBanner';
import { BillingEntitlementBanner } from './BillingEntitlementBanner';
import { UpstreamStatusBanner } from './UpstreamStatusBanner';
import { ActionsBlockedBanner } from './ActionsBlockedBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { MobileBottomNav } from './MobileBottomNav';
import { SidebarNav } from './SidebarNav';
import { UserMenu } from './UserMenu';

interface AppShellContextValue {
  setPageTitle: (title: string) => void;
}

const AppShellContext = React.createContext<AppShellContextValue | null>(null);

export function usePageTitle(title: string) {
  const ctx = React.useContext(AppShellContext);
  React.useEffect(() => {
    ctx?.setPageTitle(title);
    return () => ctx?.setPageTitle('Greenlight');
  }, [ctx, title]);
}

function deriveTitle(pathname: string, items: readonly NavItem[]): string {
  const match = items.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  return match?.label ?? 'Greenlight';
}

export function AppShell() {
  const user = useCurrentUser();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [pageTitle, setPageTitle] = React.useState('Greenlight');

  const navOpts = React.useMemo(
    () => ({
      // Preview flags are stable for the page lifetime — flipping one reloads
      // (PreviewFeaturesMenu), so a mount-time read is current.
      costManagementEnabled: isPreviewFeatureEnabled('cost-management'),
      greenlightAgentsEnabled: isPreviewFeatureEnabled('greenlight-agents'),
    }),
    [user],
  );
  const items = React.useMemo(
    () => (user ? visibleNavItems(user.role, navOpts) : []),
    [user, navOpts],
  );
  const bottomSlots = React.useMemo(
    () => (user ? bottomNavSlots(user.role, navOpts) : []),
    [user, navOpts],
  );
  const derivedTitle = React.useMemo(
    () => deriveTitle(location.pathname, items),
    [location.pathname, items],
  );
  const title = pageTitle !== 'Greenlight' ? pageTitle : derivedTitle;

  React.useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  const navSheetDrag = useSheetDrag({
    onDismiss: () => setSheetOpen(false),
    enabled: sheetOpen,
    resetKey: location.pathname,
  });

  const ctx = React.useMemo(() => ({ setPageTitle }), []);

  return (
    <AppShellContext.Provider value={ctx}>
      <div
        data-testid="app-shell"
        className="flex h-[100dvh] flex-col overflow-hidden bg-background"
      >
        {user?.impersonating && (
          <ImpersonationBanner user={{ ...user, impersonating: user.impersonating }} />
        )}
        {user?.fleet_reporting && (
          <FleetReportingBanner enforcement={user.fleet_reporting} role={user.role} />
        )}
        {user?.billing_entitlement && (
          <BillingEntitlementBanner enforcement={user.billing_entitlement} role={user.role} />
        )}
        {user?.upstream_status && <UpstreamStatusBanner upstream={user.upstream_status} />}
        {user?.actions_blocked && (
          <ActionsBlockedBanner blocked={user.actions_blocked} role={user.role} />
        )}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row overflow-hidden">
          <header
            data-testid="mobile-top-bar"
            className="md:hidden h-14 flex items-center justify-between px-4 border-b border-border bg-card sticky top-0 z-30 pt-[env(safe-area-inset-top)]"
          >
            {/* Balanced spacer on the left so the centered title stays centered
              against the theme toggle on the right. Overflow nav lives solely in
              the bottom-nav "More" tab now — no hamburger here. */}
            <div className="w-11" aria-hidden />
            <div className="text-[14px] font-semibold truncate" data-testid="mobile-title">
              {title}
            </div>
            <ThemeToggle />
          </header>

          <aside
            data-testid="sidebar"
            className="hidden md:flex md:w-[64px] lg:w-[220px] shrink-0 border-r border-border bg-card flex-col"
          >
            <div
              className={cn('h-14 flex items-center border-b border-border', 'px-3 lg:px-4 gap-2')}
            >
              <Link
                to="/apps"
                className="flex items-center text-foreground"
                aria-label="Greenlight home"
              >
                <Logo variant="symbol" decorative className="hidden h-5 md:block lg:hidden" />
                <Logo decorative className="h-5 md:hidden lg:block" />
              </Link>
            </div>
            <div className="flex-1 overflow-y-auto">
              <SidebarNav items={items} variant="sidebar" />
            </div>
            {user && (
              <div className="border-t border-border p-3 hidden lg:block">
                <UserMenu
                  email={user.email}
                  role={user.role}
                  className="w-full"
                  onSendFeedback={() => setFeedbackOpen(true)}
                />
              </div>
            )}
          </aside>

          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent
              side="bottom"
              className="rounded-t-[20px] max-h-[85dvh] p-0 md:hidden flex flex-col"
              data-testid="nav-drawer"
            >
              <GLSheetGrabHandle
                ref={navSheetDrag.handleRef}
                onPointerDown={navSheetDrag.onPointerDown}
                onPointerMove={navSheetDrag.onPointerMove}
                onPointerUp={navSheetDrag.onPointerUp}
                onPointerCancel={navSheetDrag.onPointerCancel}
                className="shrink-0"
              />
              <div className="flex items-center gap-2 px-5 pt-1 pb-3">
                <Logo decorative className="h-5 text-foreground" />
                <SheetTitle className="sr-only">Greenlight</SheetTitle>
                <SheetDescription className="sr-only">Primary navigation</SheetDescription>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarNav items={items} variant="drawer" onNavigate={() => setSheetOpen(false)} />
              </div>
              <div className="mt-auto border-t border-border p-3 pb-safe flex items-center justify-between gap-2">
                {user ? (
                  <UserMenu
                    email={user.email}
                    role={user.role}
                    className="flex-1"
                    onSendFeedback={() => setFeedbackOpen(true)}
                  />
                ) : (
                  <span className="min-w-0" />
                )}
                <PreviewFeaturesMenu />
              </div>
            </SheetContent>
          </Sheet>

          <main
            data-testid="app-shell-main"
            className="min-h-0 flex-1 overflow-y-auto pb-[calc(56px+env(safe-area-inset-bottom))] md:pb-0"
          >
            <header
              data-testid="desktop-top-bar"
              className="hidden md:flex h-14 items-center justify-end gap-1 border-b border-border px-[clamp(24px,4vw,48px)] sticky top-0 z-20 bg-background/85 backdrop-blur"
            >
              <PreviewFeaturesMenu />
              <ThemeToggle />
            </header>
            <div className="max-w-[1180px] mx-auto px-4 md:px-[clamp(24px,4vw,48px)] py-6 md:py-8">
              <RouteErrorBoundary resetKey={location.pathname}>
                <Outlet />
              </RouteErrorBoundary>
            </div>
          </main>

          <MobileBottomNav slots={bottomSlots} onMore={() => setSheetOpen(true)} />

          {/* Mounted only while open so the shell never runs the submit hook (or any
              feedback query) on every page — the trigger lives in UserMenu. */}
          {user && feedbackOpen && <FeedbackDialog open onOpenChange={setFeedbackOpen} />}
        </div>
      </div>
    </AppShellContext.Provider>
  );
}
